import { isFormulaType } from '@doss/common-all'
import { Typography } from '@mui/material'
import {
  GridCellCoordinates,
  GridFooterContainer,
  GridPagination,
  GridSlotsComponentsProps,
  useGridApiContext,
  useGridApiEventHandler,
} from '@mui/x-data-grid-premium'
import { useMemo, useState } from 'react'
import { FiPlus } from 'react-icons/fi'
import { tss } from 'tss-react/mui'
import { cn } from '~/@/lib/utils'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { usePage } from '~/common/hooks/usePage'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import AddRecordDialog from '~/components/records/add-record/AddRecordDialog'
import { Button } from '~/components/ui/atoms/button/Button'
import { useTableMenuContext } from '../shared/Table/TableMenus'
import { TableUploadActions } from '../shared/Table/TableUploadActions'
import { LoadingSpinner } from '../ui/atoms/loading-spinner/LoadingSpinner'
import { useTableDetails } from './TableDetailsProvider'
import { TableFooterGridMetrics } from './TableFooterGridMetrics'
import { DetailedTableColumn, TableType } from './types'

export interface DossTableFooterProps extends NonNullable<GridSlotsComponentsProps['footer']> {
  tableId: string
  parentRowId?: string
  readOnly: boolean
  tableLoading: boolean
  tableColumns: DetailedTableColumn[]
  openTableBulkUploadDialog: boolean
  setOpenTableBulkUploadDialog: (open: boolean) => void
  totalRowCount: number
}

export type MUIDataGridCellValueTypes = string | number | boolean | Date | null | undefined

/**
 * Footer component for Doss tables. Contains pagination, grid metrics, and add record button.
 */
export const DossTableFooter = ({
  tableId,
  parentRowId,
  readOnly,
  tableLoading,
  tableColumns,
  openTableBulkUploadDialog,
  setOpenTableBulkUploadDialog,
  totalRowCount,
  ...props
}: DossTableFooterProps) => {
  const { classes } = useStyles()

  const { t } = useTypedTranslation()
  const [addRecordDialogOpen, setAddRecordDialogOpen] = useState(false)
  const apiRef = useGridApiContext()
  const { current } = apiRef
  const { allowInfiniteScroll } = useTableMenuContext()
  const { pageType } = usePage()
  const { table, tableType } = useTableDetails()
  const { permissionsManager } = useGlobalContext()
  const addRecordParentRowId = tableType === TableType.CHILD_TABLE ? parentRowId : undefined

  // Hide "Add Record" when viewing a child table standalone (no parent row context)
  const isChildTableWithoutParentRow = table?.parentTable != null && !parentRowId

  const requiredColumnIds = tableColumns
    .filter(
      (column) => !column.isNullable && !column.indexAutogenerate && !isFormulaType(column.type),
    )
    .map((column) => column.id)
  const canAddRecord = permissionsManager.canAddRowToTable(tableId, requiredColumnIds)

  // Mirror the grid's cell selection into local state so the footer re-renders when it changes.
  // The apiRef is stable, so reading unstable_getSelectedCellsAsArray() at render time is not
  // reactive on its own (especially under React Compiler auto-memoization).
  const [selectedCellsArray, setSelectedCellsArray] = useState<GridCellCoordinates[]>(() =>
    current.unstable_getSelectedCellsAsArray(),
  )
  useGridApiEventHandler(apiRef, 'cellSelectionChange', () => {
    setSelectedCellsArray(current.unstable_getSelectedCellsAsArray())
  })

  /**
   * Get column type and values for selected cells
   * Selected cells array could have elements but cell info array could be null if the selected cells do not qualify for metrics calculation
   */
  const cellInfo = useMemo(() => {
    // If any cells have an invalid row id or if there is only one cell, return null
    // This makes sure we are not displaying metrics for selected child table cells on the parent table footer (parent table apiRef is not able to get the row ids of child table cells)
    if (
      selectedCellsArray.length <= 1 ||
      // GridRowId type definition doesn't include undefined but it can be undefined
      // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
      selectedCellsArray.some((cell: GridCellCoordinates) => cell.id === undefined)
    ) {
      return null
    }

    const firstCell = selectedCellsArray[0]
    const column = current.getColumn(firstCell.field)

    const values = selectedCellsArray.map((cell: GridCellCoordinates) =>
      current.getCellValue<MUIDataGridCellValueTypes>(cell.id, cell.field),
    )

    return {
      columnType: column.type,
      values,
      singleColumn: selectedCellsArray.every(
        (cell: GridCellCoordinates) => cell.field === firstCell.field,
      ),
    }
  }, [selectedCellsArray, current])

  // If in the record view, display the upload data button in the footer for child tables
  const displayUploadDataButton = useMemo(() => {
    return pageType === 'record' && parentRowId
  }, [pageType, parentRowId])

  const handleClose = () => {
    setAddRecordDialogOpen(false)
  }

  const handleOpen = () => {
    setAddRecordDialogOpen(true)
  }

  return (
    <GridFooterContainer className={cn(classes.container)} {...props}>
      <div className="flex">
        {!readOnly && canAddRecord && !isChildTableWithoutParentRow && (
          <Button onClick={handleOpen} size="small" startIcon={<FiPlus />} variant="ghost">
            <Typography variant="body1">{t('table.add_record')}</Typography>
          </Button>
        )}
        {displayUploadDataButton && table && canAddRecord && (
          <TableUploadActions
            setOpenTableBulkUploadDialog={setOpenTableBulkUploadDialog}
            size="xsmall"
            variant="ghost"
          />
        )}
      </div>

      <div className="flex items-center justify-end gap-3">
        {tableLoading && (
          <div className="flex items-center gap-3">
            <LoadingSpinner type="navigation" />
            <div className="text-sm text-gray-9">{t('table.loading')}</div>
          </div>
        )}
        {/* Cell Selection Metrics Component */}
        <TableFooterGridMetrics
          cellValues={cellInfo?.values ?? []}
          columnType={cellInfo?.singleColumn ? cellInfo.columnType : undefined}
          selectedCellsArray={selectedCellsArray}
          totalRowCount={totalRowCount}
        />
        {/* For infinite scroll, remove pagination */}
        {!allowInfiniteScroll && <GridPagination />}
      </div>
      {addRecordDialogOpen && (
        <AddRecordDialog
          onClose={handleClose}
          open={addRecordDialogOpen}
          parentRowId={addRecordParentRowId}
          tableId={tableId}
        />
      )}
    </GridFooterContainer>
  )
}

const useStyles = tss.create(({ theme }) => {
  return {
    container: {
      paddingInlineStart: theme.spacing(1.5),
      height: '49px',
      minHeight: '49px',
      borderBottomWidth: '0px',
    },
  }
})
